import { useEffect, useRef } from "react";
import { sanitizePageSchema, schemaTypes, routeRobots } from "./schemaPolicy.mjs";

// SafeJsonLd: renders a schema.org JSON-LD block into a <script> tag.
// Avoids dangerouslySetInnerHTML entirely by writing the sanitized string to
// script.textContent via ref. Input is our own JSON.stringify'd trusted schema,
// then DOMPurify-sanitized with ALLOWED_TAGS: [] as a defense-in-depth layer.
//
// Deduplication: the prerender build step also injects JSON-LD into the static
// HTML so crawlers without JS can read schemas. When React mounts on a real
// browser, any pre-existing <script type="application/ld+json"> blocks of the
// SAME top-level @type are removed first — otherwise Google Search Console flags
// "Duplicate field FAQPage" / "Duplicate field LocalBusiness".
export function SafeJsonLd({ schema }) {
  const ref = useRef(null);
  const clean = sanitizePageSchema(schema, "https://techhelpswfl.com");
  useEffect(() => {
    if (!ref.current) return;
    ref.current.textContent = JSON.stringify(clean).replace(/</g, "\\u003c");
    // Remove other JSON-LD scripts with the same @type that this component owns.
    const types = schemaTypes(clean);
    if (types.length) {
      document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
        if (el === ref.current || el.hasAttribute("data-react-jsonld") || el.hasAttribute("data-global-schema")) return;
        try {
          const parsed = JSON.parse(el.textContent || "{}");
          if (schemaTypes(parsed).some((type) => types.includes(type))) el.remove();
        } catch {
          /* ignore unparseable blocks (likely pre-rendered partial content) */
        }
      });
    }
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps
  return clean ? <script ref={ref} data-react-jsonld="true" type="application/ld+json" /> : null;
}

export function updateMetaDescription(content) {
  const el = document.querySelector('meta[name="description"]');
  if (el) el.setAttribute("content", content);
}

export function updateCanonical(pathOrUrl) {
  updateRobots(/^https?:/i.test(pathOrUrl) ? new URL(pathOrUrl).pathname : pathOrUrl);
  const base = "https://techhelpswfl.com";
  // Accept either a relative path ("/services") or a fully-qualified URL ("https://...");
  // normalize so we never double-prefix.
  const href = /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : `${base}${pathOrUrl}`;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

export function updateOgTags({ title, description, url, image, type = "website", publishedTime, modifiedTime, author, section }) {
  const base = "https://techhelpswfl.com";
  const DEFAULT_IMG = `${base}/icons/icon-512.png`;
  const ogUrl = url ? (/^https?:\/\//i.test(url) ? url : `${base}${url}`) : undefined;
  const tags = {
    "og:title": title,
    "og:description": description,
    "og:url": ogUrl,
    "og:image": image || DEFAULT_IMG,
    "og:type": type,
    "twitter:title": title,
    "twitter:description": description,
    "twitter:image": image || DEFAULT_IMG,
  };
  // Article-only tags (for blog posts) — improve Facebook/LinkedIn previews
  if (type === "article") {
    if (publishedTime) tags["article:published_time"] = publishedTime;
    if (modifiedTime) tags["article:modified_time"] = modifiedTime;
    if (author) tags["article:author"] = author;
    if (section) tags["article:section"] = section;
  } else document.querySelectorAll('meta[property^="article:"]').forEach((meta) => meta.remove());
  Object.entries(tags).forEach(([prop, content]) => {
    if (content == null) return;
    const selector = prop.startsWith("og:") || prop.startsWith("article:") ? `meta[property="${prop}"]` : `meta[name="${prop}"]`;
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      if (prop.startsWith("og:") || prop.startsWith("article:")) {
        el.setAttribute("property", prop);
      } else {
        el.setAttribute("name", prop);
      }
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  });
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `https://techhelpswfl.com${item.path}`,
    })),
  };
}

export function updateRobots(path, explicit) {
  let meta = document.querySelector('meta[name="robots"]');
  if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
  meta.content = explicit || (document.querySelector('[data-testid="not-found-page"], [data-testid="route-error-boundary"]') ? "noindex,follow" : routeRobots(path, window.location.search));
}

export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a },
    })),
  };
}

/**
 * Build a WebPage schema wrapper. Use on any top-level route for
 * Google's WebPage / BreadcrumbList / primaryImageOfPage signals.
 */
export function buildWebPageSchema({ name, description, url, image, breadcrumb }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: `https://techhelpswfl.com${url}`,
    ...(image ? { primaryImageOfPage: { "@type": "ImageObject", url: image } } : {}),
    ...(breadcrumb ? { breadcrumb } : {}),
    inLanguage: "en-US",
    isPartOf: {
      "@type": "WebSite",
      name: "I.T. Help of SWFL",
      url: "https://techhelpswfl.com",
    },
  };
}
